import { useEffect, useRef } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../app/appHooks";
import {
  loadCommunityUnits as loadReportCommunityUnits,
  setSelectedUnit,
} from "./reportsSlice";
import { CommunityTypes } from "../../helpers/constants";
import { FloatingNotificationPanel } from "../common/FloatingNotificationPanel";

import { UnitList } from "./UnitsList";
import { ReportsViewer } from "./ReportsViewer";
import { minToolbarHeight } from "../app/App";

const useStyles = makeStyles<Theme, Boolean>((theme: Theme) =>
  createStyles({
    reportsContainer: {
      display: "flex",
      height: (showBanner) =>
        `calc(100vh - ${minToolbarHeight(showBanner, theme)})`,
    },
    unitListContainer: {
      display: "flex",
      flex: "0 0 300px",
      backgroundColor: theme.palette.light.light,
    },
    reportsViewerContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      overflowY: "hidden",
      overflowX: "hidden",
      "@media (max-height: 830px)": {
        overflowY: "auto",
      },
      [theme.breakpoints.down("md")]: {
        overflowY: "auto",
      },
    },
  })
);

export default function Reports() {
  const showBanner = useAppSelector((state) => state.headerState.showBanner);

  /* ---- Hooks ---- */
  const classes = useStyles(showBanner);
  const dispatch = useDispatch();

  /* ---- Selectors ---- */
  const userUnits = useAppSelector((state) => state.headerState.units);
  const selectedCommunity = useAppSelector(
    (state) => state.headerState.selectedCommunity
  );
  const isAipCommunity =
    selectedCommunity?.community_type === CommunityTypes.aip;
  const reportsUnits = useAppSelector((state) => state.reportsState.units);
  const selectedUnit = useAppSelector(
    (state) => state.reportsState.selectedUnit
  );

  /* ---- Refs ---- */
  const containerRef = useRef<HTMLDivElement | null>(null);

  /* ---- Effects ---- */
  // Auto-select the single unit for AIP communities
  useEffect(() => {
    if (
      isAipCommunity &&
      reportsUnits &&
      reportsUnits.length > 0 &&
      selectedUnit === undefined
    ) {
      dispatch(setSelectedUnit(reportsUnits[0].id));
    }
  }, [isAipCommunity, reportsUnits, selectedUnit, dispatch]);

  // The header loads the community's units from the API, without units the user can't see
  useEffect(() => {
    if (userUnits) dispatch(loadReportCommunityUnits(userUnits));
  }, [userUnits, dispatch]);

  return (
    <div className={classes.reportsContainer}>
      {selectedCommunity && !isAipCommunity && (
        <div className={classes.unitListContainer}>
          <UnitList />
        </div>
      )}
      <div
        className={classes.reportsViewerContainer}
        ref={containerRef}
        id="viewer-container"
      >
        <ReportsViewer />
      </div>
      <FloatingNotificationPanel />
    </div>
  );
}
